import type { MatLayout } from "../model/Project";

/**
 * A small synchronous hint about the autosaved project (#1230), kept in
 * `localStorage` next to the IndexedDB save. Reading it costs well under a
 * millisecond, so the app knows before any async work whether a restore is
 * coming, how long each part of it will likely take on this device, and where
 * the mat will sit, without opening the database.
 *
 * It is a prediction, not the truth: the restore result always wins, and a
 * missing or stale hint only changes how the wait looks, never what loads.
 *
 * `index.html` carries an inline copy of `predictPhases` / `restoreMs` /
 * `isDelayedLoad` so the very first paint is already right;
 * src/storage/loadHint.test.ts keeps the two in step.
 */
export const LOAD_HINT_KEY = "leather-art:load-hint";

/** Restores predicted to take at least this many milliseconds say so: the
 *  title reads "Loading…", the Parts panel "Loading parts…", and the tools
 *  dim. A faster one shows none of that. A load predicted under it that
 *  runs this long says so then. */
export const LOADING_TEXT_MS = 150;

/** Restores predicted to take at least this many milliseconds show the
 *  progress bar, from the first paint of the mat; one predicted under it
 *  that runs this long shows it then. Independent of LOADING_TEXT_MS. */
export const PROGRESS_BAR_MS = 500;

/** A restore still running this long after it began is taken as stuck (an
 *  IndexedDB request that never answers), and the page says so and offers a
 *  reload: never sooner than STUCK_MIN_MS, and not before STUCK_FACTOR times
 *  the predicted restore, so a project that is just big doesn't trip it. */
export const STUCK_MIN_MS = 10_000;
export const STUCK_FACTOR = 4;

export function stuckAfterMs(predictedMs: number | null): number {
  return Math.max(STUCK_MIN_MS, (predictedMs ?? 0) * STUCK_FACTOR);
}

/**
 * How long each part of the last load took on this device, in milliseconds.
 * `startup` runs from navigation start until the editor (and the mat) is up
 * and the restore begins; the rest is the restore itself, in order: waiting
 * for IndexedDB, parsing the save, deriving the project, rendering it.
 */
export interface LoadPhases {
  startup: number;
  wait: number;
  parse: number;
  derive: number;
  render: number;
}

/** The restore's phases, in the order they run. */
export const RESTORE_PHASES = ["wait", "parse", "derive", "render"] as const;
export type RestorePhase = (typeof RESTORE_PHASES)[number];

export interface LoadHint {
  /** Serialized size of the last autosave, in characters. */
  bytes: number;
  /** Where the mat sat in the last autosave, so it can be laid out before
   *  the project arrives and doesn't shift when it does. */
  mat?: MatLayout;
  /** The last load's phases on this device, and the size it loaded. */
  phases?: LoadPhases;
  phaseBytes?: number;
}

export function readLoadHint(): LoadHint | null {
  try {
    const raw = localStorage.getItem(LOAD_HINT_KEY);
    if (!raw) return null;
    const hint = JSON.parse(raw) as LoadHint;
    return typeof hint?.bytes === "number" ? hint : null;
  } catch {
    return null;
  }
}

/** Forget the hint: there's no saved project to predict. */
export function clearLoadHint(): void {
  try {
    localStorage.removeItem(LOAD_HINT_KEY);
  } catch {
    /* storage unavailable */
  }
}

/** Merge `patch` into the stored hint. Best-effort: storage may be blocked. */
export function writeLoadHint(patch: Partial<LoadHint>): void {
  try {
    const next = { ...(readLoadHint() ?? { bytes: 0 }), ...patch };
    localStorage.setItem(LOAD_HINT_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable: the next load just has no prediction */
  }
}

/**
 * How long each part of this load will likely take, or null when this device
 * has never timed one. The last load's phases, with the restore's scaled by
 * how much the save grew or shrank since: reading, parsing, deriving and
 * rendering all grow with its size. Startup doesn't depend on the save.
 */
export function predictPhases(hint: LoadHint | null): LoadPhases | null {
  const p = hint?.phases;
  if (!hint || !p) return null;
  const scale =
    hint.phaseBytes && hint.phaseBytes > 0 ? hint.bytes / hint.phaseBytes : 1;
  return {
    startup: p.startup,
    wait: p.wait * scale,
    parse: p.parse * scale,
    derive: p.derive * scale,
    render: p.render * scale,
  };
}

/** The restore's predicted length: the window in which edits would be lost,
 *  which is what decides fast vs delayed (startup isn't part of it). */
export function restoreMs(phases: LoadPhases | null): number | null {
  if (!phases) return null;
  return RESTORE_PHASES.reduce((sum, k) => sum + phases[k], 0);
}

/** Whether the restore is expected to be slow enough to say "Loading…" (the
 *  delayed state) from the first paint. */
export function isDelayedLoad(predictedMs: number | null): boolean {
  return predictedMs !== null && predictedMs >= LOADING_TEXT_MS;
}

/** Whether the restore is expected to be slow enough to show the progress
 *  bar from the start. */
export function showsProgressBar(predictedMs: number | null): boolean {
  return predictedMs !== null && predictedMs >= PROGRESS_BAR_MS;
}
